import { clsx } from "clsx";
import { type CSSProperties, type FC, type PropsWithChildren } from "react";
import { type AriaProgressBarProps, useProgressBar } from "react-aria";

import { useVisuallyLoading } from "../../hooks.ts";
import style from "./LoadingSpinner.module.css";
import { LoadingSpinnerIntent } from "./settings.ts";

export interface LoadingSpinnerProps {
  inCenter?: boolean;
  leftMargin?: number; // specific for GenomeView TrackWidth
  size?: number;
  intent?: LoadingSpinnerIntent;
  debounceStart?: number;
  style?: CSSProperties;
  className?: string;
}

interface LoaderProps {
  progressBarProps: AriaProgressBarProps;
  className?: string;
  intent?: LoadingSpinnerIntent;
  size?: number;
  loaderStyle?: CSSProperties;
}

const Loader: FC<LoaderProps> = ({ progressBarProps, className, intent, size, loaderStyle }) => (
  <div
    {...progressBarProps}
    className={clsx(style.progress_circle_indeterminate, className, {
      [style.progress_circle_primary]: intent == null || intent === LoadingSpinnerIntent.Primary,
      [style.progress_circle_secondary]: intent === LoadingSpinnerIntent.Secondary,
      [style.progress_circle_danger]: intent === LoadingSpinnerIntent.Danger,
    })}
    style={{ width: size, height: size, ...loaderStyle }}
    data-testid={"loading-spinner"}
  />
);

export const LoadingSpinner: FC<PropsWithChildren<LoadingSpinnerProps>> = (props) => {
  // Fake loading for at least 500ms
  const isVisuallyLoading = useVisuallyLoading(true, props.debounceStart, 500);
  const { progressBarProps } = useProgressBar({
    isIndeterminate: true,
    "aria-label": "Loading...",
  });

  if (!isVisuallyLoading) return null;

  return props.inCenter ? (
    <div
      className={style.loading_center_container}
      style={
        props.leftMargin
          ? { transform: `translate(calc(-50% + ${Math.ceil((props.leftMargin ?? 0) / 2)}px), -50%)` }
          : undefined
      }
    >
      <Loader
        progressBarProps={progressBarProps}
        className={props.className}
        intent={props.intent}
        size={props.size}
        loaderStyle={props.style}
      />
    </div>
  ) : (
    <Loader
      progressBarProps={progressBarProps}
      className={props.className}
      intent={props.intent}
      size={props.size}
      loaderStyle={props.style}
    />
  );
};
